import React, { createContext, useContext, useEffect, useState } from 'react';
import { palettes, defaultPalette } from '../config/palettes';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(() => {
    // Check localStorage for saved preference
    const saved = localStorage.getItem('darkMode');
    if (saved !== null) {
      return JSON.parse(saved);
    }
    // Default to system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const [currentPalette, setCurrentPalette] = useState(() => {
    // Check localStorage for saved palette
    const saved = localStorage.getItem('colorPalette');
    return saved || defaultPalette;
  });

  // Apply palette colors as CSS variables
  useEffect(() => {
    const palette = palettes[currentPalette];
    if (!palette) return;

    const root = document.documentElement;

    // Apply primary colors
    Object.entries(palette.primary).forEach(([shade, color]) => {
      root.style.setProperty(`--color-primary-${shade}`, color);
    });

    // Apply accent colors
    Object.entries(palette.accent).forEach(([shade, color]) => {
      root.style.setProperty(`--color-accent-${shade}`, color);
    });

    // Save preference
    localStorage.setItem('colorPalette', currentPalette);
  }, [currentPalette]);

  useEffect(() => {
    // Apply dark mode class to document
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    // Save preference
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(prev => !prev);
  };

  const setPalette = (paletteName) => {
    if (palettes[paletteName]) {
      setCurrentPalette(paletteName);
    }
  };

  return (
    <ThemeContext.Provider value={{
      darkMode,
      toggleDarkMode,
      currentPalette,
      setPalette,
      availablePalettes: palettes
    }}>
      {children}
    </ThemeContext.Provider>
  );
};
